import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./DealerPipeline.module.scss";
import classes from "../Management.module.scss";
import {
  getDealerAdminCustomer,
  addDealerAdminCustomerNote,
  removeDealerAdminCustomerNote,
  INQUIRY_STATUS_LABELS,
  INQUIRY_TYPE_LABELS,
  formatUsd,
} from "../../../services/dealerGarage";
import PanelFrame from "./PanelFrame";
import DepositPanel from "./DepositPanel";
import BillingPanel from "./BillingPanel";
import { VehicleThumb } from "./PurchasePanel";
import { formatDate, formatPhone, vehicleLabel, errorMessage, daysInStep, sinceLabel } from "./dealerAdmin";

const TABS = [
  { id: "timeline", label: "Timeline" },
  { id: "deals", label: "Deals" },
  { id: "billing", label: "Billing" },
  { id: "saved", label: "Saved cars" },
];

const FILTERS = [
  { id: "all", label: "All" },
  { id: "conversation", label: "Conversation" },
  { id: "deals", label: "Deals" },
  { id: "money", label: "Money" },
  { id: "notes", label: "Notes" },
];

// Entries shown before "Show more".
const PAGE = 100;

// "Today", "Yesterday", "Mon, Oct 19" or "Oct 19, 2025" for older years.
const dayLabel = (date) => {
  const d = new Date(date);
  const today = new Date();
  const days = Math.round((new Date(today.toDateString()) - new Date(d.toDateString())) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString([], d.getFullYear() === today.getFullYear()
    ? { weekday: "short", month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" });
};

const timeOf = (date) => new Date(date).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const Stat = ({ label, value, sub }) => (
  <div className={styles.stat}>
    <div className={styles.statLabel}>{label}</div>
    <div className={styles.statValue}>{value}</div>
    {sub && <div className={styles.statLabel}>{sub}</div>}
  </div>
);

// One deal row in the Deals tab: the car, its state, how long it has sat there.
const DealRow = ({ record, state, onOpen }) => {
  const days = daysInStep(record);
  return (
    <li>
      <VehicleThumb record={record} className={styles.cardThumb} />
      <div className={styles.carInfo}>
        <strong>{vehicleLabel(record)}</strong>
        <div className={styles.hint}>{state}{days !== null ? ` · ${days === 0 ? "today" : `${days}d`} in this step` : ""}</div>
      </div>
      <button type="button" className={styles.btn} onClick={onOpen}>Open</button>
    </li>
  );
};

/**
 * One customer, opened from a record panel's header, the Customers list or a
 * ?customer= link: who they are and the numbers that matter, then everything
 * that happened with them (Timeline, with staff notes), their leads,
 * inspections and purchases (Deals), deposit and invoices (Billing) and the
 * cars they saved. A timeline entry or a deal opens its own panel over this
 * one; closing that comes back here.
 *
 * Props: customerRef ({ userId } | { inquiryId } | { inspectionId }), onClose(),
 * onOpenInquiry(id), onOpenInspection(id), onOpenPurchase(id), refreshKey
 */
const CustomerPanel = ({ customerRef, onClose, onOpenInquiry, onOpenInspection, onOpenPurchase, refreshKey }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("timeline");
  const [filter, setFilter] = useState("all");
  const [shown, setShown] = useState(PAGE);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [ledgerVersion, setLedgerVersion] = useState(0);

  const refKey = JSON.stringify(customerRef);
  const load = useCallback(() => {
    getDealerAdminCustomer(JSON.parse(refKey))
      .then((res) => {
        if (res?.message === "Success" && res.data) {
          setData(res.data);
          setError("");
        } else {
          setError(errorMessage(res, "Could not load the customer."));
        }
      })
      .catch(() => setError("Could not load the customer."));
  }, [refKey]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const timeline = useMemo(() => (data ? data.timeline : []), [data]);
  const filtered = useMemo(
    () => (filter === "all" ? timeline : timeline.filter((e) => e.group === filter)),
    [timeline, filter]
  );

  const canClose = () => !note.trim() || window.confirm("Close without saving your note?");

  if (!data) {
    return (
      <PanelFrame
        label="Customer"
        onClose={onClose}
        header={<h2>{error || "Loading..."}</h2>}
      />
    );
  }

  const { customer, stats } = data;
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(" ") || customer.email;

  const addNote = async () => {
    const body = note.trim();
    if (!body) return;
    setBusy(true);
    const res = await addDealerAdminCustomerNote(customer.ref, body).catch(() => null);
    setBusy(false);
    if (res?.message === "Note added" && res.data) {
      setData((d) => ({ ...d, timeline: [res.data, ...d.timeline] }));
      setNote("");
    } else {
      alert(errorMessage(res, "Could not save the note."));
    }
  };

  const removeNote = async (entry) => {
    if (!window.confirm("Remove this note?")) return;
    const res = await removeDealerAdminCustomerNote(entry.noteId).catch(() => null);
    if (res?.message === "Note removed") {
      setData((d) => ({ ...d, timeline: d.timeline.filter((e) => e.id !== entry.id) }));
    } else {
      alert(errorMessage(res, "Could not remove the note."));
    }
  };

  // The panel a timeline entry or a deal opens, if it belongs to one.
  const opener = (recordType, recordId) => {
    if (!recordId) return null;
    if (recordType === "inquiry") return () => onOpenInquiry(recordId);
    if (recordType === "inspection") return () => onOpenInspection(recordId);
    if (recordType === "purchase") return () => onOpenPurchase(recordId);
    return null;
  };

  const counts = {
    timeline: timeline.length,
    deals: data.inquiries.length + data.inspections.length + data.purchases.length,
    saved: data.favorites.length,
  };

  const header = (
    <>
      <div className={styles.headerTop}>
        <h2>{name}</h2>
      </div>
      <div className={styles.hint}>
        {customer.email}
        {customer.otherEmails.length > 0 && ` (also ${customer.otherEmails.join(", ")})`}
        {customer.phone && <> · <a href={`tel:${customer.phone}`} className={classes.clickableCell}>{formatPhone(customer.phone)}</a></>}
        {customer.port ? ` · ${customer.port} port` : ""}
        {customer.city ? ` · ${customer.city}` : ""}
      </div>
      <div className={styles.headerChips}>
        <span className={`${styles.chip} ${styles.chipMuted}`}>
          {customer.hasAccount ? (customer.isGuest ? "Guest account" : "Account") : "Lead only - no account"}
        </span>
        {customer.isVipPlus && <span className={`${styles.chip} ${styles.chipGood}`}>VIP+</span>}
        {customer.emailBounced && <span className={`${styles.chip} ${styles.chipBad}`}>Email bounces - call instead</span>}
        {customer.emailOptOut && <span className={`${styles.chip} ${styles.chipMuted}`}>Opted out of marketing email</span>}
      </div>
      <div className={styles.statRow}>
        <Stat label="Deposit on file" value={customer.hasAccount ? formatUsd(customer.depositBalance, { cents: false }) : "-"} />
        <Stat label="Paid to date" value={formatUsd(stats.paidToDate, { cents: false })} />
        <Stat label="Open deals" value={stats.openDeals} />
        <Stat
          label="Customer since"
          value={stats.customerSince ? new Date(stats.customerSince).toLocaleDateString([], { month: "short", year: "numeric" }) : "-"}
          sub={stats.firstTouch || ""}
        />
      </div>
    </>
  );

  const tabs = (
    <div className={styles.tabs} role="tablist">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          className={`${styles.tab} ${tab === t.id ? styles.tabActive : ""}`}
          onClick={() => setTab(t.id)}
        >
          {t.label}
          {counts[t.id] > 0 && <span className={styles.tabCount}>{counts[t.id]}</span>}
        </button>
      ))}
    </div>
  );

  let day = "";
  return (
    <PanelFrame label={`Customer: ${name}`} onClose={onClose} canClose={canClose} header={header} tabs={tabs}>
      {tab === "timeline" && (
        <>
          <div className={styles.noteBox}>
            <textarea
              className={classes.formInput}
              rows={2}
              maxLength={2000}
              value={note}
              placeholder={`A note about ${customer.firstName || "them"} - only staff see it`}
              aria-label="New note"
              onChange={(e) => setNote(e.target.value)}
            />
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !note.trim()} onClick={addNote}>
              Add note
            </button>
          </div>
          <div className={styles.filterChips}>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                aria-pressed={filter === f.id}
                className={`${styles.filterChip} ${filter === f.id ? styles.filterChipActive : ""}`}
                onClick={() => { setFilter(f.id); setShown(PAGE); }}
              >
                {f.label}
              </button>
            ))}
          </div>
          {filtered.length === 0 && <p className={styles.hint}>Nothing here yet.</p>}
          <ol className={styles.timeline}>
            {filtered.slice(0, shown).map((entry) => {
              const label = dayLabel(entry.at);
              const heading = label !== day ? label : null;
              day = label;
              const open = opener(entry.recordType, entry.recordId);
              const Body = open ? "button" : "div";
              return (
                <React.Fragment key={entry.id}>
                  {heading && <li className={styles.timelineDay}>{heading}</li>}
                  <li className={`${styles.timelineItem} ${styles[`tone_${entry.tone}`] || ""}`}>
                    <span className={`${styles.timelineDot} ${styles[`dot_${entry.group}`] || ""}`} aria-hidden="true" />
                    <Body
                      {...(open ? { type: "button", onClick: open, title: "Open" } : {})}
                      className={`${styles.timelineMain} ${open ? styles.timelineLink : ""}`}
                    >
                      <span className={styles.timelineTitle}>{entry.title}</span>
                      {entry.body && <span className={styles.timelineBody}>{entry.body}</span>}
                    </Body>
                    <span className={styles.timelineMeta} title={new Date(entry.at).toLocaleString()}>
                      {entry.actor && entry.actor.name ? `${entry.actor.name} · ` : ""}{timeOf(entry.at)}
                      {entry.kind === "note" && (
                        <button type="button" className={styles.btnText} onClick={() => removeNote(entry)}>Remove</button>
                      )}
                    </span>
                  </li>
                </React.Fragment>
              );
            })}
          </ol>
          {filtered.length > shown && (
            <button type="button" className={styles.btn} onClick={() => setShown((n) => n + PAGE)}>
              Show more ({filtered.length - shown})
            </button>
          )}
        </>
      )}

      {tab === "deals" && (
        <>
          <div className={styles.panelSection}>
            <h3>Leads ({data.inquiries.length})</h3>
            {data.inquiries.length === 0 ? <p className={styles.hint}>No leads.</p> : (
              <ul className={styles.carList}>
                {data.inquiries.map((l) => (
                  <DealRow
                    key={l._id}
                    record={l}
                    state={[
                      INQUIRY_TYPE_LABELS[l.type] || "Inquiry",
                      l.archivedAt ? "Archived" : (INQUIRY_STATUS_LABELS[l.status] || l.status),
                      l.extraCars ? `+${l.extraCars} more ${l.extraCars === 1 ? "car" : "cars"}` : "",
                      `since ${formatDate(l.createdAt)}`,
                    ].filter(Boolean).join(" · ")}
                    onOpen={() => onOpenInquiry(l._id)}
                  />
                ))}
              </ul>
            )}
          </div>
          <div className={styles.panelSection}>
            <h3>Inspections ({data.inspections.length})</h3>
            {data.inspections.length === 0 ? <p className={styles.hint}>No inspections.</p> : (
              <ul className={styles.carList}>
                {data.inspections.map((i) => (
                  <DealRow
                    key={i._id}
                    record={i}
                    state={[
                      i.status,
                      i.scheduledFor && i.status === "Scheduled" ? formatDate(i.scheduledFor) : "",
                      i.decision && i.decision.value ? (i.decision.value === "buy" ? "buying" : "passed") : "",
                    ].filter(Boolean).join(" · ")}
                    onOpen={() => onOpenInspection(i._id)}
                  />
                ))}
              </ul>
            )}
          </div>
          <div className={styles.panelSection}>
            <h3>Purchases ({data.purchases.length})</h3>
            {data.purchases.length === 0 ? <p className={styles.hint}>No purchases.</p> : (
              <ul className={styles.carList}>
                {data.purchases.map((p) => (
                  <DealRow
                    key={p._id}
                    record={p}
                    state={[p.status, p.price ? formatUsd(p.price, { cents: false }) : "", p.source === "auction" ? "auction" : ""].filter(Boolean).join(" · ")}
                    onOpen={() => onOpenPurchase(p._id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {tab === "billing" && (customer.hasAccount ? (
        <div className={`${styles.grid2} ${classes.stackedForm}`} style={{ alignItems: "start" }}>
          <div className={styles.panelSection}>
            <h3>Deposit</h3>
            <DepositPanel userId={customer.userId} refreshKey={ledgerVersion} />
          </div>
          <div className={styles.panelSection}>
            <h3>All invoices</h3>
            <BillingPanel
              scope={{ userId: customer.userId }}
              defaultKind="deposit"
              onChange={() => { setLedgerVersion((v) => v + 1); load(); }}
            />
          </div>
        </div>
      ) : (
        <p className={styles.hint}>
          No account yet, so no deposit or invoices. One opens by itself when you record a deposit, an invoice or a sale on their lead.
        </p>
      ))}

      {tab === "saved" && (data.favorites.length === 0 ? <p className={styles.hint}>No saved cars.</p> : (
        <ul className={styles.carList}>
          {data.favorites.map((f) => (
            <li key={f._id}>
              <VehicleThumb record={f} className={styles.cardThumb} />
              <div className={styles.carInfo}>
                {f.vehicle && f.vehicle.url
                  ? <a href={f.vehicle.url} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>{vehicleLabel(f)}</a>
                  : <strong>{vehicleLabel(f)}</strong>}
                <div className={styles.hint}>
                  Saved {sinceLabel(f.createdAt)}
                  {f.vehicle && f.vehicle.usdPrice ? ` · listed ${formatUsd(f.vehicle.usdPrice, { cents: false })}` : ""}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ))}
    </PanelFrame>
  );
};

export default CustomerPanel;
